import React, { useState, useEffect } from 'react';
import axiosPost from '../helpers/axios-helper';

import '../styles/Home.css';
import { MdChevronRight } from 'react-icons/md';
import { FaMapPin } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import EventCalendar from '../components/EventCalendar';

export default function Home() {
  const [storyOpen, setStoryOpen] = useState(false);
  const [articles, setArticles] = useState([]);
  const [destinations, setDestinations] = useState([]);

  const slugify = (str) => {
    try {
      return (str || '')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    } catch { return ''; }
  };

  // Fetch countries
  useEffect(() => {
    const controller = new AbortController();
    axiosPost(
      'countries/list',
      {},
      { include_inactive: 1 },
      (res) => {
        const list = Array.isArray(res) ? res : (res?.data || []);
        const mapped = list.map((c, i) => ({
          id: c.id ?? i + 1,
          name: c.name || c.title || '-',
          continent: c.continent || 'Caribbean',
          image: c.image || '',
          active: parseInt(c.active ?? 1)
        }));
        setDestinations(mapped);
      },
      controller
    );
    return () => controller.abort();
  }, []);

  // Fetch latest 4 articles
  useEffect(() => {
    const controller = new AbortController();
    axiosPost(
      'articles/list',
      {},
      {
        status: 'published',
        per_page: 4,
        sort: 'date_published',
        order: 'desc'
      },
      (res) => {
        const items = res?.data?.items || [];
        const mapped = items.map((article) => ({
          id: article.id,
          title: article.title || '',
          description: article.excerpt || '',
          author: (typeof article.author === 'object' ? article.author?.name : article.author) || 'Outcaribbean',
          image: article.featured_image_url || '',
          slug: article.slug || ''
        }));
        setArticles(mapped);
      },
      controller
    );
    return () => controller.abort();
  }, []);


  const renderArticles = () => {
    if (!articles || articles.length === 0) {
      return (
        <div className='articles'>
          <div style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
            Loading articles...
          </div>
        </div>
      );
    }

    const highlight = articles[0];

    const list_articles = articles.filter((article, index) => index > 0).map((article, index) => {
      const articleLink = article.slug ? `/article/${article.slug}` : `/blog?id=${article.id}`;
      
      return (
        <Link key={article.id} className='article-list-item' to={articleLink}>
          <div className='article-item-image' style={{backgroundImage: `url(${article.image})`, backgroundSize: 'cover'}}></div>
          <div className='article-item-info'>
            <div> 
              <div className='article-item-title'>{article.title}</div>
              <div className='article-item-description'>{article.description}</div>
            </div>
            
            <div className='article-item-author'>Written by {article.author}</div>
          </div>
        </Link>
      )
    })

    const highlightLink = highlight.slug ? `/article/${highlight.slug}` : `/blog?id=${highlight.id}`;

    return (
      <div className='articles'>
        <Link to={highlightLink} className='articles-highlight' style={{backgroundImage: `url(${highlight.image})`, backgroundSize: 'cover'}}>
          <div className='article-highlight-overlay'>
            <div className='article-highlight-title'>{highlight.title}</div>
            <div className='article-highlight-description'>{highlight.description}</div>
            <div className='article-highlight-author'>Written by {highlight.author}</div>
          </div>
        </Link>
        <div className='articles-list-container'>
          {list_articles}
        </div>

      </div>
    )


  }

  const renderDestinations = () => {

    const render_destinations = destinations.map((destination, index) => {
      const isActive = destination.active === 1;
      const content = (
        <div className='destination' style={{backgroundImage: `url(${destination.image})`, backgroundSize: 'cover', cursor: isActive ? 'pointer' : 'default', opacity: isActive ? 1 : 0.85}}>
          <div className='destination-cover'>
            <div className='destination-name-home'>{destination.name}</div>
            {!isActive && (
              <div style={{
                marginTop: '8px',
                background: 'rgba(0,0,0,0.7)',
                color: '#fff',
                padding: '6px 12px',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>Coming Soon</div>
            )}
          </div>
        </div>
      );

      return isActive ? (
        <Link
          key={destination.id}
          to={`/country/${slugify(destination.name)}`}
          style={{textDecoration: 'none'}}
        >
          {content}
        </Link>
      ) : (
        <div key={destination.id} style={{textDecoration: 'none'}}>
          {content}
        </div>
      );
    })

    return (
      <div className='destinations-container'>
        { render_destinations }
      </div>
    )

  }

  return (
    <div>
      <div className='landing-section' style={{backgroundImage: `url(${process.env.PUBLIC_URL + '/assets/landing-bg.png'})`, backgroundSize: 'cover'}}>
        <div className='landing-overlay'>
          <div className='landing-text'>
            WHERE PARADISE MEETS PARADOX. <br/>
            <div className='landing-text-sub'>
              Navigate with intelligence and heart.
            </div>
          </div>
        </div>
      </div>

      <div className='home-about-container'>
        <div className='home-about'>
          <div className='home-about-title'>OUR STORY</div>

          <div className='home-about-body'>
          <p className='home-about-headline'>Every paradise has a paradox. Ours began in Curaçao.</p>

          <div className='home-about-description'>
            <p>The Caribbean exists in beautiful contradiction: breathtaking landscapes shaped by complex histories, where desert islands and rainforest coasts share the same sea, and where centuries of migration, resistance, and survival created cultures the world loves but rarely understands. For Queer travelers, and for those of us coming home, this paradox becomes personal: how do you fall in love with places that don’t always make space for who you are?</p>
            <p>Outcaribbean exists to answer that question, not with false reassurance or fear-based warnings, but with knowledge and lived experience. Queer-owned and based in the Caribbean, we illuminate the stories behind each destination and curate what is truly worth your time, with honest insight into how each place receives Queer travelers. Through our Queer community app, you connect with Queer locals and fellow travelers who know the region from within.</p>

            {/* The rest continues straight on from the teaser, in place,
                rather than repeating the headline on another page. */}
            {storyOpen && (
              <div className='home-about-more' id='our-story-more'>
                <p>In a world where being Queer is still a crime in dozens of countries, the Caribbean holds both extremes. Some places hold on to old laws against Queer love. Others have become places of real welcome, where activists fought and won.</p>
                <p>In Curaçao and Aruba, marriage equality was not a gift. It was won in court.</p>
                <p>Ours is a region shaped by the world’s great crossings: African, Indian, Chinese, Javanese, and European heritage meeting the Indigenous peoples who were here first, and creating something found nowhere else. You taste it in a Surinamese roti, hear it in Curaçaoan tambú, feel it in carnival and in spiritual traditions that have survived centuries, and see it in a new generation of artists reclaiming their history.</p>
                <p>Queer and trans people have always been part of this. So have the feminists, thinkers, healers, artists, and allies, Queer and not, who made space for others to be free. Their lives rarely made the guidebooks.</p>
                <p>Outcaribbean brings them to the foreground.</p>
                <p>Most of the Caribbean’s people are Black, Indigenous, and people of color. At Outcaribbean, their Queer voices are not an afterthought. They lead the story. And the door is open to everyone who comes with respect, curiosity, and care.</p>
                <p>For some, the Caribbean is a discovery. For others, it is where the family story began: the place your parents left, the language your grandmother still speaks, the home you return to and wonder whether there is room there for all of you.</p>
                <p>Sometimes there is. Sometimes there isn’t yet. We will tell you honestly, and help you find your way.</p>
                <p>We share knowledge about Caribbean heritage, history, and culture, and about the people who changed laws and minds, all rooted in lived experience. We curate hotels, restaurants, places, and experiences worth your time, and tell you honestly where you will be genuinely welcomed and where to move with awareness. Our Queer community app connects you with Queer locals who make each place what it is, and with fellow Queer travelers, from the islands to the mainland.</p>
                <p>We don’t just tell you about the Caribbean. We help you experience it. Taste Sunday roti at a local favorite. Lose yourself in carnival. Discover art made on the islands. Stand where history happened, with a local guide who knows the story. Wherever we can, we champion Queer-owned and welcoming local businesses.</p>
                <p>Whether you are arriving for the first time, coming home, or living here every day, this is the Caribbean told from within. Come as you are.</p>
              </div>
            )}

            <p className='home-about-promise'>Travel with knowledge. Arrive with heart.</p>
          </div>

          {!storyOpen && (
            <button
              type='button'
              className='home-about-more-btn'
              aria-expanded={storyOpen}
              aria-controls='our-story-more'
              onClick={() => setStoryOpen(true)}
            >
              Read more
              <MdChevronRight size={18} />
            </button>
          )}
          </div>
        </div>

      </div>

      {/* <div className='container'>
        <div className='section-title'>Events</div>
        
        <EventCalendar limit={4} />
      </div> */}
      
      <div className='container'>
        <div className='section-title'>Articles</div>

        {renderArticles()}
      </div>

      <div className='container'>
        <div className='section-title'>Destinations</div>
        { renderDestinations() }
      </div>
    </div>
  )
}
